import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Select } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';

import { IFeaturePermissions } from '../interfaces/feature-permissions.interface';
import { IUser } from '../interfaces/users/user.interface';
import { PermissionsService } from '../services/internal/permissions.service';
import { AuthState } from '../store/state/auth.state';
import { HomeworkSchoolState } from '../store/state/homework-school.state';
import { MealsState } from '../store/state/meals.state';
import { NurseryState } from '../store/state/nursery.state';

@Injectable({
    providedIn: 'root',
})
export class PermissionsGuard implements CanActivate {
    @Select(AuthState.user) public connectedUser$: Observable<IUser>;
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(MealsState.getIsMealsSettingsLoaded) public mealsSettingsLoaded$: Observable<boolean>;
    @Select(NurseryState.getIsNurserySettingsLoaded) public nurserySettingsLoaded$: Observable<boolean>;
    @Select(HomeworkSchoolState.getIsHomeworkSchoolSettingsLoaded) public homeworkSettingsLoaded$: Observable<boolean>;
    @Select(AuthState.isSchoolSettingsLoaded) public isSchoolSettingsLoaded$: Observable<boolean>;

    constructor(private permissionService: PermissionsService, private router: Router, private localize: LocalizeRouterService) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        const permission: any = route.data?.permission;
        return combineLatest([
            this.connectedUser$,
            this.currentUser$,
            this.mealsSettingsLoaded$,
            this.nurserySettingsLoaded$,
            this.homeworkSettingsLoaded$,
            this.isSchoolSettingsLoaded$,
        ]).pipe(
            skipWhile(
                ([connectedUser, currentUser, mealsSettingsLoaded, nurserySettingsLoaded, homeworkSettingsLoaded, schoolSettingsLoaded]: [
                    IUser,
                    IUser,
                    boolean,
                    boolean,
                    boolean,
                    boolean
                ]) =>
                    currentUser === null ||
                    connectedUser === null ||
                    mealsSettingsLoaded === false ||
                    nurserySettingsLoaded === false ||
                    homeworkSettingsLoaded === false ||
                    schoolSettingsLoaded === false
            ),
            map(([connectedUser, currentUser]: [IUser, IUser, boolean, boolean, boolean, boolean]) => {
                const isAccessAllowed: boolean = this.permissionService.checkPermission(
                    connectedUser.accessLevel,
                    currentUser.accessLevel,
                    permission as IFeaturePermissions
                );
                return isAccessAllowed
                    ? isAccessAllowed
                    : this.router.createUrlTree([this.localize.translateRoute('/messaging/received-messages')]);
            })
        );
    }
}
